import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Trash2, Loader2, AlertCircle } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { formatCurrency } from "@/lib/utils";

interface StartupCostItem {
  _id?: Id<"bidStartupCosts">;
  description: string;
  quantity: number;
  unitCost: number;
  total: number;
  category?: string;
}

interface MonthlyCostItem {
  _id?: Id<"bidMonthlyCosts">;
  description: string;
  quantity: number;
  unitCost: number;
  total: number;
  type: "labor" | "supply" | "operational";
  category?: string;
}

interface CostSummary {
  totalStartupCost: number;
  totalLaborCost: number;
  totalSupplyCost: number;
  totalOperationalCost: number;
  totalMonthlyCost: number;
  totalAnnualCost: number;
}

interface CostToolProps {
  bidId?: Id<"bids">;
  onClose?: () => void;
  onSave?: (data: any) => void;
}

export function CostTool({ bidId, onClose, onSave }: CostToolProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("startup");
  const [startupCosts, setStartupCosts] = useState<StartupCostItem[]>([]);
  const [monthlyCosts, setMonthlyCosts] = useState<MonthlyCostItem[]>([]);
  const [isLoading, setIsLoading] = useState(bidId ? true : false);

  // Fetch data from new schema
  const startupCostsData = useQuery(
    api.bidStartupCosts.getBidStartupCosts,
    bidId ? { bidId } : "skip"
  );

  const monthlyCostsData = useQuery(
    api.bidMonthlyCosts.getBidMonthlyCosts,
    bidId ? { bidId } : "skip"
  );

  const costSummary = useQuery(api.bidCostSummaries.getBidCostSummary, bidId ? { bidId } : "skip");

  // Mutations for new schema
  const addStartupCostMutation = useMutation(api.bidStartupCosts.addStartupCost);
  const deleteStartupCostMutation = useMutation(api.bidStartupCosts.deleteStartupCost);
  const addMonthlyCostMutation = useMutation(api.bidMonthlyCosts.addMonthlyCost);
  const deleteMonthlyCostMutation = useMutation(api.bidMonthlyCosts.deleteMonthlyCost);
  const recalculateCostSummaryMutation = useMutation(
    api.bidCostSummaries.recalculateBidCostSummary
  );

  // Recalculate cost summary when component loads
  useEffect(() => {
    if (bidId) {
      console.log("Initial cost summary calculation for bid:", bidId);
      updateCostSummary();
    }
  }, [bidId, recalculateCostSummaryMutation]);

  // Load data from new schema
  useEffect(() => {
    if (!bidId) {
      setIsLoading(false);
      return;
    }

    // Load data from new schema
    if (startupCostsData && monthlyCostsData) {
      try {
        // Convert to our internal format
        const mappedStartupCosts = startupCostsData.map((item: any) => ({
          _id: item._id,
          description: item.description,
          quantity: item.quantity,
          unitCost: item.unitCost,
          total: item.total,
          category: item.category,
        }));

        const mappedMonthlyCosts = monthlyCostsData.map((item: any) => ({
          _id: item._id,
          description: item.description,
          quantity: item.quantity,
          unitCost: item.unitCost,
          total: item.total,
          type: item.type,
          category: item.category,
        }));

        setStartupCosts(mappedStartupCosts);
        setMonthlyCosts(mappedMonthlyCosts);
        setIsLoading(false);
      } catch (error) {
        console.error("Error processing cost data:", error);
        setStartupCosts([]);
        setMonthlyCosts([]);
        setIsLoading(false);
      }
    } else if (startupCostsData === null && monthlyCostsData === null && bidId) {
      // No costs found, but query completed
      setIsLoading(false);
    }
  }, [bidId, startupCostsData, monthlyCostsData]);

  const [newStartupItem, setNewStartupItem] = useState<Partial<StartupCostItem>>({
    description: "",
    quantity: 1,
    unitCost: 0,
  });

  const [newMonthlyItem, setNewMonthlyItem] = useState<Partial<MonthlyCostItem>>({
    description: "",
    quantity: 1,
    unitCost: 0,
    type: "labor",
  });

  // Helper function to update cost summary
  const updateCostSummary = async () => {
    if (!bidId) return;

    try {
      console.log("Recalculating cost summary...");
      await recalculateCostSummaryMutation({ bidId });
      console.log("Cost summary updated successfully");
    } catch (error) {
      console.error("Error updating cost summary:", error);
    }
  };

  const handleAddStartupItem = async () => {
    if (!newStartupItem.description || !newStartupItem.quantity || !newStartupItem.unitCost) {
      return;
    }

    const total = (newStartupItem.quantity || 0) * (newStartupItem.unitCost || 0);

    if (bidId) {
      try {
        // Save to database first
        const newItemId = await addStartupCostMutation({
          bidId,
          description: newStartupItem.description || "",
          quantity: newStartupItem.quantity || 0,
          unitCost: newStartupItem.unitCost || 0,
          total,
          category: newStartupItem.category,
        });

        // Add to local state with the returned ID
        const newItem: StartupCostItem = {
          _id: newItemId,
          description: newStartupItem.description || "",
          quantity: newStartupItem.quantity || 0,
          unitCost: newStartupItem.unitCost || 0,
          total,
          category: newStartupItem.category,
        };

        setStartupCosts([...startupCosts, newItem]);

        // Reset form
        setNewStartupItem({
          description: "",
          quantity: 1,
          unitCost: 0,
        });

        // Update cost summary
        await updateCostSummary();

        // Notify user of successful save
        toast.success("Startup cost added and saved");

        // Call the onSave callback if provided
        if (onSave) {
          const updatedCosts = [...startupCosts, newItem];
          const summary = {
            startupCosts: updatedCosts,
            monthlyCosts,
            totalStartupCost: updatedCosts.reduce((sum, item) => sum + item.total, 0),
            totalLaborCost,
            totalSupplyCost,
            totalOperationalCost,
            totalMonthlyCost,
            totalAnnualCost,
          };
          onSave(summary);
        }
      } catch (error) {
        console.error("Error saving cost data:", error);
        toast.error("Failed to save cost data");
      }
    }
  };

  const handleAddMonthlyItem = async () => {
    if (!newMonthlyItem.description || !newMonthlyItem.quantity || !newMonthlyItem.unitCost) {
      return;
    }

    const total = (newMonthlyItem.quantity || 0) * (newMonthlyItem.unitCost || 0);

    if (bidId) {
      try {
        // Save to database first
        const newItemId = await addMonthlyCostMutation({
          bidId,
          description: newMonthlyItem.description || "",
          quantity: newMonthlyItem.quantity || 0,
          unitCost: newMonthlyItem.unitCost || 0,
          total,
          type: newMonthlyItem.type || "labor",
          category: newMonthlyItem.category,
        });

        // Add to local state with the returned ID
        const newItem: MonthlyCostItem = {
          _id: newItemId,
          description: newMonthlyItem.description || "",
          quantity: newMonthlyItem.quantity || 0,
          unitCost: newMonthlyItem.unitCost || 0,
          total,
          type: newMonthlyItem.type || "labor",
          category: newMonthlyItem.category,
        };

        const updatedCosts = [...monthlyCosts, newItem];
        setMonthlyCosts(updatedCosts);

        // Reset form
        setNewMonthlyItem({
          description: "",
          quantity: 1,
          unitCost: 0,
          type: newMonthlyItem.type,
        });

        // Calculate new totals for the callback
        const newLaborCost = updatedCosts
          .filter((item) => item.type === "labor")
          .reduce((sum, item) => sum + item.total, 0);

        const newSupplyCost = updatedCosts
          .filter((item) => item.type === "supply")
          .reduce((sum, item) => sum + item.total, 0);

        const newOperationalCost = updatedCosts
          .filter((item) => item.type === "operational")
          .reduce((sum, item) => sum + item.total, 0);

        const newMonthlyCost = newLaborCost + newSupplyCost + newOperationalCost;
        const newAnnualCost = newMonthlyCost * 12;

        // Update cost summary
        await updateCostSummary();

        // Notify user of successful save
        toast.success(
          `${newItem.type.charAt(0).toUpperCase() + newItem.type.slice(1)} cost added and saved`
        );

        // Call the onSave callback if provided
        if (onSave) {
          onSave({
            startupCosts,
            monthlyCosts: updatedCosts,
            totalStartupCost,
            totalLaborCost: newLaborCost,
            totalSupplyCost: newSupplyCost,
            totalOperationalCost: newOperationalCost,
            totalMonthlyCost: newMonthlyCost,
            totalAnnualCost: newAnnualCost,
          });
        }
      } catch (error) {
        console.error("Error saving cost data:", error);
        toast.error("Failed to save cost data");
      }
    }
  };

  const handleRemoveStartupItem = async (itemId: Id<"bidStartupCosts">) => {
    if (!bidId) return;

    try {
      // Delete from database first
      await deleteStartupCostMutation({ id: itemId });

      // Update local state
      const updatedCosts = startupCosts.filter((item) => item._id !== itemId);
      setStartupCosts(updatedCosts);

      // Update cost summary
      await updateCostSummary();

      // Notify user of successful save
      toast.success("Startup cost removed and saved");

      // Call the onSave callback if provided
      if (onSave) {
        const totalStartupCost = updatedCosts.reduce((sum, item) => sum + item.total, 0);
        onSave({
          startupCosts: updatedCosts,
          monthlyCosts,
          totalStartupCost,
          totalLaborCost,
          totalSupplyCost,
          totalOperationalCost,
          totalMonthlyCost,
          totalAnnualCost,
        });
      }
    } catch (error) {
      console.error("Error removing cost data:", error);
      toast.error("Failed to remove cost data");
    }
  };

  const handleRemoveMonthlyItem = async (itemId: Id<"bidMonthlyCosts">) => {
    if (!bidId) return;

    // Get the item type before removing it
    const itemToRemove = monthlyCosts.find((item) => item._id === itemId);
    const itemType = itemToRemove?.type || "item";

    try {
      // Delete from database first
      await deleteMonthlyCostMutation({ id: itemId });

      // Update local state
      const updatedCosts = monthlyCosts.filter((item) => item._id !== itemId);
      setMonthlyCosts(updatedCosts);

      // Calculate new totals for the callback
      const newLaborCost = updatedCosts
        .filter((item) => item.type === "labor")
        .reduce((sum, item) => sum + item.total, 0);

      const newSupplyCost = updatedCosts
        .filter((item) => item.type === "supply")
        .reduce((sum, item) => sum + item.total, 0);

      const newOperationalCost = updatedCosts
        .filter((item) => item.type === "operational")
        .reduce((sum, item) => sum + item.total, 0);

      const newMonthlyCost = newLaborCost + newSupplyCost + newOperationalCost;
      const newAnnualCost = newMonthlyCost * 12;

      // Update cost summary
      await updateCostSummary();

      // Notify user of successful save
      toast.success(
        `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} cost removed and saved`
      );

      // Call the onSave callback if provided
      if (onSave) {
        onSave({
          startupCosts,
          monthlyCosts: updatedCosts,
          totalStartupCost,
          totalLaborCost: newLaborCost,
          totalSupplyCost: newSupplyCost,
          totalOperationalCost: newOperationalCost,
          totalMonthlyCost: newMonthlyCost,
          totalAnnualCost: newAnnualCost,
        });
      }
    } catch (error) {
      console.error("Error removing cost data:", error);
      toast.error("Failed to remove cost data");
    }
  };

  const totalStartupCost = startupCosts.reduce((sum, item) => sum + item.total, 0);

  const totalLaborCost = monthlyCosts
    .filter((item) => item.type === "labor")
    .reduce((sum, item) => sum + item.total, 0);

  const totalSupplyCost = monthlyCosts
    .filter((item) => item.type === "supply")
    .reduce((sum, item) => sum + item.total, 0);

  const totalOperationalCost = monthlyCosts
    .filter((item) => item.type === "operational")
    .reduce((sum, item) => sum + item.total, 0);

  const totalMonthlyCost = totalLaborCost + totalSupplyCost + totalOperationalCost;
  const totalAnnualCost = totalMonthlyCost * 12;

  // Data is now auto-saved when items are added or removed

  return (
    <div className="bg-white p-6">
      {isLoading ? (
        <div className="flex h-64 flex-col items-center justify-center">
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-blue-500" />
          <span className="text-lg">Loading cost data...</span>
        </div>
      ) : (
        <>
          <h2 className="text-xl font-semibold">Cost Tool</h2>
          <p className="mb-6 text-gray-500">
            Calculate startup and monthly labor costs for your bid
          </p>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="startup">Startup Costs</TabsTrigger>
              <TabsTrigger value="labor">Monthly Labor Costs</TabsTrigger>
              <TabsTrigger value="supply">Monthly Supply Cost</TabsTrigger>
              <TabsTrigger value="operational">Monthly Operational Cost</TabsTrigger>
            </TabsList>

            <TabsContent value="startup" className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Add Startup Cost Item</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="startup-description">Description</Label>
                    <Input
                      id="startup-description"
                      placeholder="Enter item description"
                      value={newStartupItem.description}
                      onChange={(e) =>
                        setNewStartupItem({
                          ...newStartupItem,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="startup-quantity">Quantity</Label>
                    <Input
                      id="startup-quantity"
                      type="number"
                      placeholder="Enter quantity"
                      value={newStartupItem.quantity || ""}
                      onChange={(e) =>
                        setNewStartupItem({
                          ...newStartupItem,
                          quantity: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="startup-unit-cost">Unit Cost ($)</Label>
                    <Input
                      id="startup-unit-cost"
                      type="number"
                      placeholder="Enter unit cost"
                      value={newStartupItem.unitCost || ""}
                      onChange={(e) =>
                        setNewStartupItem({
                          ...newStartupItem,
                          unitCost: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>
                <Button
                  onClick={handleAddStartupItem}
                  className="mt-4 gap-2"
                  disabled={
                    !newStartupItem.description ||
                    !newStartupItem.quantity ||
                    !newStartupItem.unitCost
                  }
                >
                  <Plus className="h-4 w-4" /> Add Item
                </Button>
              </Card>

              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Startup Costs</h2>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Unit Cost</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {startupCosts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-4 text-center text-gray-500">
                            No startup costs added yet. Add items above to calculate costs.
                          </TableCell>
                        </TableRow>
                      ) : (
                        startupCosts.map((item) => (
                          <TableRow key={item._id?.toString() || Math.random().toString()}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell>${item.unitCost.toFixed(2)}</TableCell>
                            <TableCell>${item.total.toFixed(2)}</TableCell>
                            <TableCell>
                              {item._id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveStartupItem(item._id!)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
                <div className="mt-4 flex items-center justify-between rounded-md bg-gray-50 p-4">
                  <span className="font-semibold">Total Startup Cost:</span>
                  <span className="text-xl font-bold">${totalStartupCost.toFixed(2)}</span>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="labor" className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Add Monthly Labor Cost Item</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="monthly-description">Description</Label>
                    <Input
                      id="monthly-description"
                      placeholder="Enter item description"
                      value={newMonthlyItem.description}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          description: e.target.value,
                          type: "labor",
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monthly-quantity">Quantity</Label>
                    <Input
                      id="monthly-quantity"
                      type="number"
                      placeholder="Enter quantity"
                      value={newMonthlyItem.quantity || ""}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          quantity: parseFloat(e.target.value) || 0,
                          type: "labor",
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monthly-unit-cost">Unit Cost ($)</Label>
                    <Input
                      id="monthly-unit-cost"
                      type="number"
                      placeholder="Enter unit cost"
                      value={newMonthlyItem.unitCost || ""}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          unitCost: parseFloat(e.target.value) || 0,
                          type: "labor",
                        })
                      }
                    />
                  </div>
                </div>
                <Button
                  onClick={handleAddMonthlyItem}
                  className="mt-4 gap-2"
                  disabled={
                    !newMonthlyItem.description ||
                    !newMonthlyItem.quantity ||
                    !newMonthlyItem.unitCost
                  }
                >
                  <Plus className="h-4 w-4" /> Add Item
                </Button>
              </Card>

              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Monthly Labor Costs</h2>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Unit Cost</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyCosts.filter((item) => item.type === "labor").length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-4 text-center text-gray-500">
                            No monthly labor costs added yet. Add items above to calculate costs.
                          </TableCell>
                        </TableRow>
                      ) : (
                        monthlyCosts
                          .filter((item) => item.type === "labor")
                          .map((item) => (
                            <TableRow key={item._id?.toString() || Math.random().toString()}>
                              <TableCell>{item.description}</TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>${item.unitCost.toFixed(2)}</TableCell>
                              <TableCell>${item.total.toFixed(2)}</TableCell>
                              <TableCell>
                                {item._id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveMonthlyItem(item._id!)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between rounded-md bg-gray-50 p-4">
                    <span className="font-semibold">Total Monthly Labor Cost:</span>
                    <span className="text-xl font-bold">${totalLaborCost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-md bg-blue-50 p-4">
                    <span className="font-semibold">Total Annual Labor Cost:</span>
                    <span className="text-xl font-bold text-blue-700">
                      ${(totalLaborCost * 12).toFixed(2)}
                    </span>
                  </div>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="supply" className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Add Monthly Supply Cost Item</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="supply-description">Description</Label>
                    <Input
                      id="supply-description"
                      placeholder="Enter item description"
                      value={newMonthlyItem.description}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          description: e.target.value,
                          type: "supply",
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supply-quantity">Quantity</Label>
                    <Input
                      id="supply-quantity"
                      type="number"
                      placeholder="Enter quantity"
                      value={newMonthlyItem.quantity || ""}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          quantity: parseFloat(e.target.value) || 0,
                          type: "supply",
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supply-unit-cost">Unit Cost ($)</Label>
                    <Input
                      id="supply-unit-cost"
                      type="number"
                      placeholder="Enter unit cost"
                      value={newMonthlyItem.unitCost || ""}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          unitCost: parseFloat(e.target.value) || 0,
                          type: "supply",
                        })
                      }
                    />
                  </div>
                </div>
                <Button
                  onClick={handleAddMonthlyItem}
                  className="mt-4 gap-2"
                  disabled={
                    !newMonthlyItem.description ||
                    !newMonthlyItem.quantity ||
                    !newMonthlyItem.unitCost
                  }
                >
                  <Plus className="h-4 w-4" /> Add Item
                </Button>
              </Card>

              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Monthly Supply Costs</h2>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Unit Cost</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyCosts.filter((item) => item.type === "supply").length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-4 text-center text-gray-500">
                            No monthly supply costs added yet. Add items above to calculate costs.
                          </TableCell>
                        </TableRow>
                      ) : (
                        monthlyCosts
                          .filter((item) => item.type === "supply")
                          .map((item) => (
                            <TableRow key={item._id?.toString() || Math.random().toString()}>
                              <TableCell>{item.description}</TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>${item.unitCost.toFixed(2)}</TableCell>
                              <TableCell>${item.total.toFixed(2)}</TableCell>
                              <TableCell>
                                {item._id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveMonthlyItem(item._id!)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between rounded-md bg-gray-50 p-4">
                    <span className="font-semibold">Total Monthly Supply Cost:</span>
                    <span className="text-xl font-bold">{formatCurrency(totalSupplyCost)}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-md bg-blue-50 p-4">
                    <span className="font-semibold">Total Annual Supply Cost:</span>
                    <span className="text-xl font-bold text-blue-700">
                      {formatCurrency(totalSupplyCost * 12)}
                    </span>
                  </div>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="operational" className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Add Monthly Operational Cost Item</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="operational-description">Description</Label>
                    <Input
                      id="operational-description"
                      placeholder="Enter item description"
                      value={newMonthlyItem.description}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          description: e.target.value,
                          type: "operational",
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="operational-quantity">Quantity</Label>
                    <Input
                      id="operational-quantity"
                      type="number"
                      placeholder="Enter quantity"
                      value={newMonthlyItem.quantity || ""}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          quantity: parseFloat(e.target.value) || 0,
                          type: "operational",
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="operational-unit-cost">Unit Cost ($)</Label>
                    <Input
                      id="operational-unit-cost"
                      type="number"
                      placeholder="Enter unit cost"
                      value={newMonthlyItem.unitCost || ""}
                      onChange={(e) =>
                        setNewMonthlyItem({
                          ...newMonthlyItem,
                          unitCost: parseFloat(e.target.value) || 0,
                          type: "operational",
                        })
                      }
                    />
                  </div>
                </div>
                <Button
                  onClick={handleAddMonthlyItem}
                  className="mt-4 gap-2"
                  disabled={
                    !newMonthlyItem.description ||
                    !newMonthlyItem.quantity ||
                    !newMonthlyItem.unitCost
                  }
                >
                  <Plus className="h-4 w-4" /> Add Item
                </Button>
              </Card>

              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Monthly Operational Costs</h2>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Unit Cost</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyCosts.filter((item) => item.type === "operational").length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-4 text-center text-gray-500">
                            No monthly operational costs added yet. Add items above to calculate
                            costs.
                          </TableCell>
                        </TableRow>
                      ) : (
                        monthlyCosts
                          .filter((item) => item.type === "operational")
                          .map((item) => (
                            <TableRow key={item._id?.toString() || Math.random().toString()}>
                              <TableCell>{item.description}</TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>${item.unitCost.toFixed(2)}</TableCell>
                              <TableCell>${item.total.toFixed(2)}</TableCell>
                              <TableCell>
                                {item._id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveMonthlyItem(item._id!)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between rounded-md bg-gray-50 p-4">
                    <span className="font-semibold">Total Monthly Operational Cost:</span>
                    <span className="text-xl font-bold">${totalOperationalCost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-md bg-blue-50 p-4">
                    <span className="font-semibold">Total Annual Operational Cost:</span>
                    <span className="text-xl font-bold text-blue-700">
                      ${(totalOperationalCost * 12).toFixed(2)}
                    </span>
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Buttons removed as requested */}
        </>
      )}
    </div>
  );
}
